import type { LDFlagSet } from 'launchdarkly-js-client-sdk';

import { getLaunchDarklyUserKeyCookieValue } from '../cookies';

import { FakeLaunchDarklyFlagContextProvider } from './ContextProvider';

/**
 * IClientSideFlagsResponse is the JSON schema for the response from the
 * /api/v1/testing/client_side_flags endpoints.
 */
interface IClientSideFlagsResponse {
  generated_at: string;
  // The typing of this data is strange. It's not likely that each item is an
  // LDFlagSet, but, for our purposes, the first is and that's what we care
  // about. See https://github.com/launchdarkly/js-sdk-common/issues/70 for more
  // about this odd typing.

  flag_data: LDFlagSet;
}

interface Props {
  children?: React.ReactNode;
}

export async function FakeLaunchDarklyProviderHoc(): Promise<React.FC> {
  // This is only run in local development. In production, LaunchDarkly feeds us
  // what we want, but we don't want a remote dependency when hacking locally.
  // We also can't use useQuery here because this is during React bootstrap.
  const clientSideFlags: IClientSideFlagsResponse = await fetch(
    process.env.REACT_APP_CLIENT_SIDE_FLAGS_API_FOR_LOCAL_DEV ?? '/api/v1/testing/client_side_flags'
  ).then((response) => {
    if (!response.ok) {
      throw new Error(
        `Non-200 error code returned when fetching /api/v1/testing/client_side_flags for LaunchDarkly local dev set up. Was ${response.status}`
      );
    }
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');
    if (!isJson) {
      throw new Error(
        `Non-JSON response returned when fetching /api/v1/testing/client_side_flags for LaunchDarkly local dev set up. Has Content-Type '${contentType}'`
      );
    }
    return response.json();
  });

  const userKey = getLaunchDarklyUserKeyCookieValue();
  const user = userKey ? { key: userKey } : { key: 'missing-ld-user-key-but-maybe-okay-for-now' };
  const flags = clientSideFlags.flag_data;

  const FakeLdFlagContextProvider: React.FC<Props> = ({ children }) => (
    <FakeLaunchDarklyFlagContextProvider flags={flags} user={user}>
      {children}
    </FakeLaunchDarklyFlagContextProvider>
  );
  return Promise.resolve(FakeLdFlagContextProvider);
}
